import { Path } from 'react-hook-form';
import { IntlShape } from 'react-intl';

import { employerSelectSelfPaid } from '@typing/Constants';
import { lightenColor } from '@utils/colorUtils';

type Error = {
  errors: string[];
  field: string;
};

export type ThingWithErrors = {
  errors: Error[];
};

export type ErrorSetter<FormType> = (field: Path<FormType>, options: { message: string }) => void;

export type HandleMutationArgs<FormType> = {
  data: ThingWithErrors | ThingWithErrors[] | null | undefined;
  intl: IntlShape;
  onError?: () => void;
  onSuccess: () => void;
  setBaseErrors: (errors: string[]) => void;
  setError: ErrorSetter<FormType>;
  txBase: string;
};

export const handleMutationResponse = <FormType>(args: HandleMutationArgs<FormType>) => {
  const { data, intl, onError, onSuccess, setBaseErrors, setError, txBase } = args;

  let useData: ThingWithErrors[] | null | undefined;

  if (Array.isArray(data)) {
    useData = data;
  } else if (data) {
    useData = [data];
  }

  let noErrors = true;
  useData?.forEach(data => {
    if (data?.errors && data?.errors?.length > 0) {
      noErrors = false;
      data.errors.forEach(fieldError => {
        const messages = fieldError.errors.map(error =>
          intl.formatMessage({ id: `${txBase}.${fieldError.field}.${error}` })
        );

        if (fieldError.field === 'base') {
          setBaseErrors(messages);
        } else {
          setError(fieldError.field as Path<FormType>, { message: messages.join(' ') });
        }
      });
    }
  });
  if (noErrors) {
    onSuccess();
  } else {
    onError?.();
  }
};

export const searchSelectStyles = (paddingLeft: number, contentUnder = false, addStyles = false) => ({
  container: (base: any, _state: any) => ({
    ...base,
    textAlign: 'left'
  }),
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor:
      addStyles || state.isFocused || contentUnder ? 'var(--ion-color-white)' : 'var(--ion-color-gray-25)',
    borderColor: state.isFocused ? 'var(--ion-color-primary-300)' : 'var(--ion-color-gray-200)',
    borderRadius: contentUnder ? '4px 4px 0px 0px' : addStyles ? '4px' : '4px',
    borderStyle: 'solid',
    borderWidth: contentUnder && !state.isFocused ? '1px 1px 0 1px' : 1,
    boxShadow: 'none',
    outline: state.isFocused ? '2px solid var(--ion-color-primary-300)' : 'none',
    overflow: 'visible',
    paddingBottom: '11px',
    paddingLeft,
    paddingTop: '11px',
    zIndex: state.isFocused ? 50 : undefined,
    // eslint-disable-next-line sort-keys, @typescript-eslint/naming-convention
    '&:hover': {
      border: state.isFocused && !contentUnder ? '1px solid var(--ion-color-primary-300)' : ''
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '@media only screen and (max-width: 767px)': {
      ...base['@media only screen and (max-width: 767px)'],
      borderRadius: '100px',
      paddingBottom: '0px',
      paddingLeft: paddingLeft - 10,
      paddingRight: '45px',
      paddingTop: '0px'
    }
  }),
  group: (base: any) => ({
    ...base,
    borderBottom: '1px solid var(--ion-color-gray-200)'
  }),
  groupHeading: (base: any) => ({
    ...base,
    padding: 0
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: 0
  }),
  input: (base: any) => ({
    ...base,
    color: 'var(--ion-color-gray)'
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: 12,
    boxShadow: '0px 0px 10px #D5D5D5',
    padding: 20
  }),
  menuPortal: (base: any) => ({ ...base, borderRadius: 12, maxWidth: addStyles ? '540px' : undefined, zIndex: 9999 }),
  multiValue: (base: any, { data }: any) => {
    if (data.color) {
      return {
        ...base,
        backgroundColor: data.color
      };
    }
    return base;
  },
  multiValueLabel: (base: any, { data }: any) => {
    if (data.color) {
      return {
        ...base,
        color: 'var(--ion-color-white)'
      };
    }
    return base;
  },
  multiValueRemove: (base: any, { data }: any) => {
    if (data.color) {
      return {
        ...base,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ':hover': {
          backgroundColor: lightenColor(data.color, 10),
          color: 'white'
        },
        backgroundColor: lightenColor(data.color, 5),
        color: 'white'
      };
    }
    return base;
  },
  option: (base: any, { data, isActive, isDisabled, isFocused, isSelected }: any) => {
    if (data.color) {
      return {
        ...base,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ':active': {
          ...base[':active'],
          backgroundColor: !isDisabled ? (isSelected ? data.color : lightenColor(data.color, 60)) : undefined
        },
        backgroundColor: isDisabled
          ? undefined
          : isSelected
            ? data.color
            : isFocused
              ? lightenColor(data.color, 55)
              : undefined,
        borderRadius: 100,
        color: isDisabled ? '#ccc' : isSelected ? 'white' : data.color,
        cursor: isDisabled ? 'not-allowed' : 'default',
        paddingLeft: 24
      };
    }
    return {
      ...base,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ':active': {
        ...base[':active'],
        backgroundColor: 'var(--ion-color-primary-50)'
      },
      backgroundColor: isDisabled
        ? undefined
        : isSelected
          ? `var(--ion-color-primary-50)`
          : isActive
            ? `var(--ion-color-primary-50)`
            : isFocused
              ? 'var(--ion-color-primary-50)'
              : undefined,
      borderRadius: 100,
      color: isDisabled
        ? 'var(--ion-color-gray-500-shade)'
        : isSelected
          ? 'var(--ion-color-primary)'
          : 'var(--ion-color-primary)',
      cursor: 'pointer',
      paddingLeft: 24
    };
  },
  placeholder: (base: any) => ({
    ...base,
    color: 'var(--ion-color-primary)'
  }),
  valueContainer: (base: any) => ({
    ...base,
    paddingBottom: 0,
    paddingTop: 0
  })
});

export const selectStyles = (size: 'normal' | 'large' = 'normal') => ({
  container: (base: any, _state: any) => ({
    ...base,
    textAlign: 'left'
  }),
  control: (base: any, state: any) => {
    const controlStyles = {
      ...base,
      backgroundColor: 'var(--ion-color-white)',
      borderColor: 'var(--ion-color-gray-200)',
      borderRadius: '5px',
      borderStyle: 'solid',
      borderWidth: 1,
      outline: state.isFocused ? '2px solid var(--ion-color-primary-300)' : 'none'
    };
    if (size === 'normal') {
      controlStyles.minHeight = '32px';
      controlStyles.paddingBottom = '0px';
      controlStyles.paddingTop = '0px';
    } else if (size === 'large') {
      controlStyles.paddingBottom = '8px';
      controlStyles.paddingTop = '8px';
    }
    return controlStyles;
  },
  dropdownIndicator: (base: any, _state: any) => {
    const dropdownIndicatorStyles = { ...base };
    if (size === 'normal') {
      dropdownIndicatorStyles.padding = '0 8px';
    }
    return dropdownIndicatorStyles;
  },
  indicatorsContainer: (base: any, _state: any) => {
    const indicatorsContainerStyles = { ...base };
    if (size === 'normal') {
      indicatorsContainerStyles.padding = '0 8px';
    }
    return indicatorsContainerStyles;
  },
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  multiValue: (base: any, { data }: any) => {
    if (data.color) {
      return {
        ...base,
        backgroundColor: data.color
      };
    }
    return base;
  },
  multiValueLabel: (base: any, { data }: any) => {
    if (data.color) {
      return {
        ...base,
        color: 'var(--ion-color-white)'
      };
    }
    return base;
  },
  multiValueRemove: (base: any, { data }: any) => {
    if (data.color) {
      return {
        ...base,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ':hover': {
          backgroundColor: lightenColor(data.color, 10),
          color: 'white'
        },
        backgroundColor: lightenColor(data.color, 5),
        color: 'white'
      };
    }
    return base;
  },
  option: (base: any, { data, isDisabled, isFocused, isSelected }: any) => {
    if (data.color) {
      return {
        ...base,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ':active': {
          ...base[':active'],
          backgroundColor: !isDisabled ? (isSelected ? data.color : lightenColor(data.color, 60)) : undefined
        },
        backgroundColor: isDisabled
          ? undefined
          : isSelected
            ? data.color
            : isFocused
              ? lightenColor(data.color, 55)
              : undefined,
        color: isDisabled ? '#ccc' : isSelected ? 'white' : data.color,
        cursor: isDisabled ? 'not-allowed' : 'default'
      };
    }
    return {
      ...base,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
          ? `var(--ion-color-primary-500)`
          : isFocused
            ? 'var(--ion-color-primary-50-tint)'
            : undefined,
      color: isDisabled
        ? 'var(--ion-color-gray-500-shade)'
        : isSelected
          ? 'var(--ion-color-white)'
          : 'var(--ion-color-primary-500)'
    };
  }
});

export const editorSelectStyles = {
  ...selectStyles,
  clearIndicator: (base: any) => ({
    ...base,
    paddingBottom: '0px',
    paddingTop: '0px'
  }),
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: 'var(--ion-color-white)',
    borderColor: 'var(--ion-color-gray-200)',
    borderRadius: '5px',
    borderStyle: 'solid',
    borderWidth: 1,
    fontSize: '14px !important',
    height: '28px',
    marginBottom: '4px',
    marginLeft: '4px',
    marginRight: '4px',
    marginTop: '4px',
    minHeight: '28px',
    outline: state.isFocused ? '2px solid var(--ion-color-primary-300)' : 'none',
    paddingBottom: '0px',
    paddingTop: '0px'
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    paddingBottom: '0px',
    paddingTop: '0px'
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    padding: '0 8px'
  }),
  input: (base: any) => ({
    ...base,
    margin: '0px 2px',
    padding: '0px'
  }),
  placeholder: (base: any) => ({
    ...base,
    whiteSpace: 'noWrap'
  })
};

type ThingWithEmployerIdAndPaid = {
  employerId?: string | null;
  selfPaid?: boolean | null;
};

export function onChangeWithPaid<Model extends ThingWithEmployerIdAndPaid>(
  onChange: (values: Model) => void,
  values: Model
) {
  if (values.employerId === employerSelectSelfPaid) {
    onChange({ ...values, employerId: '', selfPaid: true });
  } else {
    onChange({ ...values, selfPaid: false });
  }
}

export const inputSelectStyles = {
  container: (base: any, _state: any) => ({
    ...base,
    textAlign: 'left'
  }),
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: 'var(--ion-color-gray-25)',
    borderColor: 'var(--ion-color-gray-200)',
    borderRadius: '5px',
    borderStyle: 'solid',
    borderWidth: 1,
    outline: state.isFocused ? '2px solid var(--ion-color-primary-300)' : 'none',
    paddingBottom: '8px',
    paddingTop: '8px'
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    paddingBottom: '0px',
    paddingTop: '0px'
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    display: 'none'
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  multiValue: (base: any) => ({
    ...base,
    background: 'var(--ion-color-primary-300)',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: '0px 10px'
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: 'var(--ion-color-white)'
  }),
  multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  option: (base: any, { isDisabled, isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isDisabled
      ? undefined
      : isSelected
        ? `var(--ion-color-primary-500)`
        : isFocused
          ? 'var(--ion-color-primary-50-tint)'
          : undefined,
    color: isDisabled
      ? 'var(--ion-color-gray-500-shade)'
      : isSelected
        ? 'var(--ion-color-white)'
        : 'var(--ion-color-primary-500)',
    cursor: 'pointer'
  })
};

export const scheduleSelectStyles = {
  ...inputSelectStyles,
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isDisabled ? 'var(--ion-color-gray-25)' : 'var(--ion-color-white)',
    borderColor: 'var(--ion-color-gray-200)',
    borderRadius: '5px',
    borderStyle: 'solid',
    borderWidth: 1,
    outline: state.isFocused ? '2px solid var(--ion-color-primary-300)' : 'none',
    paddingBottom: '8px',
    paddingTop: '8px'
  })
};
